import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { listSessions, createSession, cancelSession, getSessionRoster, listInstructors } from './api';

const BLANK = { starts_at: '', ends_at: '', time_zone: 'America/New_York', modality: 'virtual', location: '', virtual_link: '', capacity: '', instructor_id: '' };
const FIELD = 'text-xs w-full rounded-lg bg-[#141c27] border border-[#2b3a4d] text-[#e6edf6] p-2';
const ZONES = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu'];

export default function SchedulingPanel({ conceptId }) {
  const [sessions, setSessions] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [roster, setRoster] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => listSessions(conceptId).then((r) => setSessions(r.sessions || [])).catch(() => toast.error('Could not load sessions'));
  useEffect(() => { load(); listInstructors().then((r) => setInstructors(r.instructors || [])).catch(() => {}); }, [conceptId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.starts_at) { toast.error('Start time is required'); return; }
    setBusy(true);
    try {
      const payload = { ...form, capacity: form.capacity ? parseInt(form.capacity, 10) : null, instructor_id: form.instructor_id || null };
      await createSession(conceptId, payload);
      toast.success('Session scheduled');
      setForm(BLANK);
      await load();
    } catch (e) {
      toast.error(e?.message || 'Could not schedule');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this session and notify attendees?')) return;
    try { await cancelSession(id); toast.success('Canceled'); await load(); }
    catch (e) { toast.error(e?.message || 'Cancel failed'); }
  };

  const showRoster = async (id) => {
    try { const r = await getSessionRoster(id); setRoster((m) => ({ ...m, [id]: r })); }
    catch { toast.error('Could not load roster'); }
  };

  return (
    <div className="pb-4 mb-4 border-b border-[#1d2634]">
      <div className="text-[10px] uppercase tracking-wide text-[#7d8aa0] mb-2">Live sessions</div>

      <div className="grid grid-cols-2 gap-2">
        <label className="text-[11px] text-[#8a95a8]">Starts
          <input type="datetime-local" value={form.starts_at} onChange={(e) => set('starts_at', e.target.value)} className={FIELD} /></label>
        <label className="text-[11px] text-[#8a95a8]">Ends
          <input type="datetime-local" value={form.ends_at} onChange={(e) => set('ends_at', e.target.value)} className={FIELD} /></label>
        <label className="text-[11px] text-[#8a95a8]">Time zone
          <select value={form.time_zone} onChange={(e) => set('time_zone', e.target.value)} className={FIELD}>
            {ZONES.map((z) => <option key={z} value={z}>{z.replace('America/', '').replace('_', ' ')}</option>)}
          </select></label>
        <label className="text-[11px] text-[#8a95a8]">Modality
          <select value={form.modality} onChange={(e) => set('modality', e.target.value)} className={FIELD}>
            <option value="virtual">Virtual</option><option value="in_person">In person</option><option value="hybrid">Hybrid</option>
          </select></label>
        <label className="text-[11px] text-[#8a95a8]">Instructor
          <select value={form.instructor_id} onChange={(e) => set('instructor_id', e.target.value)} className={FIELD}>
            <option value="">Unassigned</option>
            {instructors.map((i) => <option key={i.id} value={i.id}>{i.name || i.email}</option>)}
          </select></label>
        <label className="text-[11px] text-[#8a95a8] col-span-2">Join link
          <input value={form.virtual_link} onChange={(e) => set('virtual_link', e.target.value)} className={FIELD} /></label>
        <label className="text-[11px] text-[#8a95a8]">Location
          <input value={form.location} onChange={(e) => set('location', e.target.value)} className={FIELD} /></label>
        <label className="text-[11px] text-[#8a95a8]">Capacity (optional)
          <input type="number" value={form.capacity} onChange={(e) => set('capacity', e.target.value)} className={FIELD} /></label>
      </div>
      <button onClick={create} disabled={busy} className="mt-2 text-xs font-semibold px-3 py-1.5 rounded-lg bg-orange-500 text-white disabled:opacity-40">
        {busy ? 'Scheduling…' : 'Schedule session'}
      </button>

      <div className="space-y-2 mt-3">
        {sessions.map((s) => (
          <div key={s.id} className="border border-[#2b3a4d] rounded p-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] text-[#c7d0e0] font-semibold">{new Date(s.starts_at).toLocaleString()} · {s.status}</div>
                <div className="text-[10px] text-[#8a95a8]">{s.modality}{s.instructor ? ` · ${s.instructor.name}` : ''} · {s.seats_taken}/{s.effective_capacity} seats</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => showRoster(s.id)} className="text-[11px] text-[#5ea9ff]">roster</button>
                {s.status === 'scheduled' && <button onClick={() => cancel(s.id)} className="text-[11px] text-orange-400">cancel</button>}
              </div>
            </div>
            {roster[s.id] && (
              <ul className="mt-1 text-[10px] text-[#8a95a8] list-disc pl-4">
                {roster[s.id].roster.map((r) => <li key={r.registration_id}>{r.attendee || r.invited_email} - {r.status}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
